import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { DreamToRealityLPStyles } from './DreamToRealityLP.styled';
import LazyLoad from '@components/Shared/LazyLoad/LazyLoad';
import ProgressIndicator from '@components/DreamToReality/DreamToRealityLP/ProgressIndicator';
import IntroSection from '@components/DreamToReality/DreamToRealityLP/IntroSection';
import Section from '@components/DreamToReality/DreamToRealityLP/Section';
import ComparisonSlider from '@components/DreamToReality/DreamToRealityLP/ComparisonSlider/ComparisonSlider';
import LandItFrame from '@components/DreamToReality/DreamToRealityLP/LandItFrame';

import data from '@components/DreamToReality/DreamToRealityLP/DreamToRealityData.json';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import { getSiteUrl } from '@utils/config';

function useForceUpdate() {
  const [value, setValue] = useState(0);
  return () => setValue(value + 1);
}

const DreamToRealityLP = () => {
  const router = useRouter();
  const [smallScreen, setSmallScreen] = useState(false);

  useForceUpdate();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const windowWidth =
        window.innerWidth < window.screen.availWidth
          ? window.innerWidth
          : window.screen.availWidth;
      if (windowWidth <= 970) setSmallScreen(true);
    }
  }, []);

  return (
    <DreamToRealityLPStyles>
      <Head htmlAttributes={{ lang: 'en' }}>
        <title>Dream To Reality | Clayton Homes</title>
        <meta
          property="og:image"
          content={`${getSiteUrl(
            '/public-assets/images/dream-to-reality/poster_have-it-made.jpg'
          )}`}
        />
        <meta
          name="description"
          content="Building affordable, quality homes since 1956 as one of America's leading home builders. Come discover our selection of mobile, modular and manufactured homes today!"
        />
        <link rel="canonical" href={`${getSiteUrl(router.pathname)}`} />
      </Head>

      <div>
        {!smallScreen ? <ProgressIndicator smallScreen={smallScreen} /> : null}

        <IntroSection
          header={data[0].header}
          blurb={data[0].blurb}
          smallScreen={smallScreen}
        />

        <LazyLoad>
          <Section
            header={data[1].header}
            blurb={data[1].blurb}
            cta={{
              ...data[1].cta,
              onClick: () => {
                pushGTMEvent('ch.cta_click', 'dtr_clayton_unbuilt', {
                  location: 'Dream to Reality',
                  category: 'brand_education',
                  action: 'button',
                  label: 'dtr_clayton_unbuilt',
                });
              },
            }}
            bgVideo="https://www.claytonhomes.com/videos/dreamtoreality/unbuilt_trim_03a_1.mp4"
            bgImage={null}
            poster={'/public-assets/images/dream-to-reality/unbuilt.jpg'}
            gaLabel={data[1].gaLabel}
            offsetBg
            smallScreen={smallScreen}
          />
        </LazyLoad>

        <LazyLoad>
          <Section
            header={data[2].header}
            blurb={data[2].blurb}
            cta={{
              ...data[2].cta,
              onClick: () => {
                pushGTMEvent('ch.cta_click', 'dtr_interactive_floor_plan', {
                  location: 'Dream to Reality',
                  category: 'home_education',
                  action: 'button',
                  label: 'dtr_interactive_floor_plan',
                });
              },
            }}
            bgVideo={null}
            bgImage={
              '/public-assets/images/dream-to-reality/interactive-floor-plan.jpg'
            }
            poster={null}
            gaLabel={data[2].gaLabel}
            offsetBg
            smallScreen={smallScreen}
          />
        </LazyLoad>

        <LazyLoad>
          <Section
            header={data[3].header}
            blurb={data[3].blurb}
            cta={{
              ...data[3].cta,
              onClick: () => {
                pushGTMEvent('ch.cta_click', 'dtr_ifp_view_homes', {
                  location: 'Dream to Reality',
                  category: 'home_search',
                  action: 'button',
                  label: 'dtr_ifp_view_homes',
                });
              },
            }}
            bgVideo="https://www.claytonhomes.com/videos/dreamtoreality/dream/Seeing-is-Believing_cr_02_Conform2_1.mp4"
            bgImage={null}
            poster={
              '/public-assets/images/dream-to-reality/poster_seeing-is-believing.jpg'
            }
            gaLabel={data[3].gaLabel}
            smallScreen={smallScreen}
          />
        </LazyLoad>

        <LazyLoad>
          <Section
            header={data[4].header}
            blurb={data[4].blurb}
            cta={{
              ...data[4].cta,
              onClick: () => {
                pushGTMEvent('ch.cta_click', 'dtr_interactive_facility_tour', {
                  location: 'Dream to Reality',
                  category: 'brand_education',
                  action: 'button',
                  label: 'dtr_interactive_facility_tour',
                });
              },
            }}
            bgVideo={null}
            bgImage={null}
            gaLabel={data[4].gaLabel}
            smallScreen={smallScreen}
          >
            <ComparisonSlider />
          </Section>
        </LazyLoad>

        <LazyLoad>
          <Section
            header={data[5].header}
            blurb={data[5].blurb}
            cta={{
              ...data[5].cta,
              onClick: () => {
                pushGTMEvent('ch.cta_click', 'dtr_find_land', {
                  location: 'Dream to Reality',
                  category: 'land_search',
                  action: 'button',
                  label: 'dtr_find_land',
                });
              },
            }}
            linkClass={data[5].cta.class}
            bgVideo={null}
            bgImage={'/public-assets/images/dream-to-reality/land-it-image.jpg'}
            poster={null}
            gaLabel={data[5].gaLabel}
            smallScreen={smallScreen}
          >
            <LandItFrame />
          </Section>
        </LazyLoad>

        <LazyLoad>
          <Section
            header={data[6].header}
            blurb={data[6].blurb}
            cta={{
              ...data[6].cta,
              onClick: () => {
                pushGTMEvent('ch.cta_click', 'dtr_clayton_connect', {
                  location: 'Dream to Reality',
                  category: 'home_education',
                  action: 'button',
                  label: 'dtr_clayton_connect',
                });
              },
            }}
            bgVideo="https://www.claytonhomes.com/videos/dreamtoreality/dream/Home-Delivery_cr_03_Conform2_1.mp4"
            bgImage={null}
            poster={
              '/public-assets/images/dream-to-reality/poster_home-delivered.jpg'
            }
            gaLabel={data[6].gaLabel}
            smallScreen={smallScreen}
          />
        </LazyLoad>

        <LazyLoad>
          <Section
            header={data[7].header}
            blurb={data[7].blurb}
            cta={{
              ...data[7].cta,
              onClick: () => {
                pushGTMEvent('ch.cta_click', 'dtr_view_homes', {
                  location: 'Dream to Reality',
                  category: 'home_search',
                  action: 'button',
                  label: 'dtr_view_homes',
                });
              },
            }}
            bgVideo="https://www.claytonhomes.com/videos/dreamtoreality/dream/Have-it-Made_cr_01_Conform2_1.mp4"
            bgImage={null}
            poster={
              '/public-assets/images/dream-to-reality/poster_have-it-made.jpg'
            }
            gaLabel={data[7].gaLabel}
            smallScreen={smallScreen}
          />
        </LazyLoad>
      </div>
    </DreamToRealityLPStyles>
  );
};

export default DreamToRealityLP;
